import { useEffect, useRef, useState } from "react";

/**
 * Custom hook for creating a typewriter animation effect
 *
 * This hook animates text by typing it out character by character, pausing,
 * then deleting it before moving to the next text. Perfect for hero sections,
 * landing pages, or any UI that needs dynamic text animation.
 *
 * @param {UseTypewriterProps} props - Configuration options for the typewriter
 * @returns {string} The current animated text to display
 *
 * @example
 * // Basic usage
 * function Hero() {
 *   const text = useTypewriter({
 *     texts: ["Hello World", "Welcome", "Let's Build"],
 *   });
 *   return <h1>{text}</h1>;
 * }
 *
 * @example
 * // Advanced usage with custom speeds and looping
 * function AnimatedTitle() {
 *   const text = useTypewriter({
 *     texts: ["Frontend Developer", "React Enthusiast", "UI/UX Designer"],
 *     typingSpeed: 100,      // Type at 100ms per character
 *     deletingSpeed: 50,     // Delete at 50ms per character
 *     pauseDuration: 2000,   // Pause for 2 seconds after typing
 *     loop: true,            // Loop through texts infinitely
 *     uppercase: true        // Convert text to uppercase
 *   });
 *   return <h2>{text}</h2>;
 * }
 *
 * @remarks
 * - Uses singleton pattern to prevent multiple animations running simultaneously
 * - Automatically cleans up timers on unmount to prevent memory leaks
 * - Text sequence: Type → Pause → Delete → (repeat for next text)
 * - Last text won't be deleted if loop is false
 */
function useTypewriter({ texts, typingSpeed, deletingSpeed, pauseDuration, loop, uppercase }: UseTypewriterProps) {
  const [typingText, setTypingText] = useState("");

  // Store interval reference to clear it later and prevent memory leaks
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Singleton pattern: ensure only one typing sequence runs at a time
  const isRunningRef = useRef(false);

  useEffect(() => {
    // Guard: prevent multiple sequences from running simultaneously
    if (isRunningRef.current) return;

    // Clean up any existing timer before starting
    if (timerRef.current) clearInterval(timerRef.current);
    isRunningRef.current = true;

    /**
     * Types out the given text character by character
     * @param text - The text to type out
     * @returns Promise that resolves when typing is complete
     */
    const typing = async (text: string) => {
      return new Promise<void>((resolve) => {
        let isEndOfType = false;

        timerRef.current = setInterval(() => {
          // Check if we've reached the end
          if (isEndOfType) {
            clearInterval(timerRef.current!);
            timerRef.current = null;
            resolve();
            return;
          }

          setTypingText((prev) => {
            // Check if next character will be the last one
            if (prev.length + 1 === text.length) {
              isEndOfType = true;
            }
            // Append next character using previous text length as index
            return `${prev}${uppercase ? text[prev.length].toUpperCase() : text[prev.length]}`;
          });
        }, typingSpeed || 150);
      });
    };

    /**
     * Deletes the current text character by character (from end to start)
     * @returns Promise that resolves when all characters are deleted
     */
    const deleting = async () => {
      return new Promise<void>((resolve) => {
        if (timerRef.current) clearInterval(timerRef.current);

        timerRef.current = setInterval(() => {
          setTypingText((prev) => {
            // Stop when all characters are deleted
            if (prev.length === 0) {
              clearInterval(timerRef.current!);
              timerRef.current = null;
              resolve();
              return prev;
            }
            // Remove last character
            return prev.slice(0, -1);
          });
        }, deletingSpeed || 100);
      });
    };

    /**
     * Pauses execution for specified duration
     * @param duration - Duration in milliseconds
     * @returns Promise that resolves after the duration
     */
    const pause = (duration: number) => {
      return new Promise<void>((resolve) => {
        setTimeout(resolve, duration);
      });
    };

    /**
     * Main sequence: orchestrates typing, pausing, and deleting for all texts
     * Runs once or loops infinitely based on the `loop` prop
     */
    const runTypewriterSequence = async () => {
      do {
        // Iterate through each text in the array
        for (let i = 0; i < texts.length; i++) {
          const text = texts[i];

          // Type the current text
          await typing(text);

          // Pause after typing completes
          await pause(pauseDuration || 1000);

          // Delete text before moving to next one (unless it's the last text and not looping)
          if (i < texts.length - 1 || loop) {
            await deleting();
            await pause(500); // Short pause before next text
          }
        }
      } while (loop); // Repeat if loop is enabled

      // Mark as not running when sequence completes
      isRunningRef.current = false;
    };

    // Start the typewriter sequence
    runTypewriterSequence();

    // Cleanup function: runs when component unmounts or dependencies change
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      isRunningRef.current = false;
    };
  }, [texts, typingSpeed, deletingSpeed, pauseDuration, loop, uppercase]);

  return typingText;
}

type UseTypewriterProps = {
  texts: string[]; // Array of texts to animate through
  typingSpeed?: number; // Milliseconds per character when typing (default: 150ms)
  deletingSpeed?: number; // Milliseconds per character when deleting (default: 100ms)
  pauseDuration?: number; // Pause duration after typing completes (default: 1000ms)
  loop?: boolean; // Whether to loop through texts infinitely
  uppercase?: boolean; // Whether to convert text to uppercase
};

type UseTypewriterReturn = ReturnType<typeof useTypewriter>;

export type { UseTypewriterProps, UseTypewriterReturn };
export default useTypewriter;
